import React, {useEffect, useMemo} from "react"
import classNames from "classnames"
import {useAccessToken, useUserType} from "../../services/auth"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useToasts} from "react-toast-notifications"
import {useQueryState} from "use-location-state"
import {startCase, toLower} from "lodash"
import {Cents} from "../../resources/transaction"
import {usePaging} from "../../hooks/usePaging"
import {Filter} from "../Filter/Filter"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {downloadFile} from "../../utilities/file"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {ReceivedPaymentsTable} from "./ReceivedPaymentsTable"
import {findReceivedPayments, ReceivedPaymentStatus, ReceivedPaymentTypeFilters} from "../../resources/receivedPayments"
import SearchBox from "../SearchBox/SearchBox"
import {useEsc} from "../../hooks/useEsc"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {Org} from "../../resources/org"
import {findOrgs} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {ErrorDocument} from "../../resources/common"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {IncomingRealTimePaymentStatus} from "../../resources/incomingRealTimePayment"

export enum ReceivedPaymentsColumns {
	id = "Id",
	org = "Org",
	type = "Type",
	direction = "Direction",
	accountId = "Account",
	customer = "Customer",
	companyName = "Company Name",
	amount = "Amount",
	status = "Status",
	createdAt = "Created At",
	completionDate = "Completion Date",
}

export type AllowedReceivedPaymentsColumns =
	| ReceivedPaymentsColumns.id
	| ReceivedPaymentsColumns.org
	| ReceivedPaymentsColumns.type
	| ReceivedPaymentsColumns.direction
	| ReceivedPaymentsColumns.accountId
	| ReceivedPaymentsColumns.customer
	| ReceivedPaymentsColumns.companyName
	| ReceivedPaymentsColumns.amount
	| ReceivedPaymentsColumns.status
	| ReceivedPaymentsColumns.createdAt
	| ReceivedPaymentsColumns.completionDate

export interface ReceivedPaymentsProps {
	accountId?: string
	customerId?: string
	token?: string
	limit?: number
	includedColumns: AllowedReceivedPaymentsColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	showTabsOnHeader?: boolean
	enableOrgFilter?: boolean
	enableStatusFilter?: boolean
	enableTypeFilter?: boolean
	enableAmountFilter?: boolean
	enableExport?: boolean
	disableDrawer?: boolean
	enableSearchBox?: boolean
}

export default function ReceivedPayments({
	limit = 25,
	token,
	includedColumns,
	fullHeight,
	showTabsOnHeader = false,
	enableTitle,
	accountId,
	customerId,
	enableOrgFilter = false,
	enableStatusFilter = false,
	enableTypeFilter = false,
	enableAmountFilter = false,
	enableExport = true,
	disableDrawer = false,
	enableSearchBox = true,
}: ReceivedPaymentsProps) {
	const prefix = "receivedPayments"
	const accessToken = token ?? useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const userType = useUserType()
	const {addToast} = useToasts()
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")

	const allStatuses = [
		ReceivedPaymentStatus.Advanced,
		ReceivedPaymentStatus.Completed,
		ReceivedPaymentStatus.Pending,
		ReceivedPaymentStatus.PendingReview,
		ReceivedPaymentStatus.Returned,
		ReceivedPaymentStatus.MarkedForReturn,
		IncomingRealTimePaymentStatus.Rejected,
		IncomingRealTimePaymentStatus.TimedOut,
	]

	const statusesOptions = new Map(allStatuses.map((v) => [v, startCase(v)]))
	const typesOptions = new Map(
		[ReceivedPaymentTypeFilters.ACH, ReceivedPaymentTypeFilters.Wire, ReceivedPaymentTypeFilters.RTP].map((v) => [
			startCase(toLower(v)),
			v,
		])
	)
	const [statuses, setStatuses] = useQueryState(
		`${prefix}-filter[status]`,
		enableStatusFilter ? (accountId ? allStatuses : [ReceivedPaymentStatus.Pending]) : allStatuses
	)
	const [types, setTypes] = useQueryState<string[]>(`${prefix}-filter[type]`, [])
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search by Customer Id", 500, onSearch)
	const [selectedReceivedPaymentId, setSelectedReceivedPaymentId] = useQueryState("receivedPaymentId", "")
	useEsc(() => setSelectedReceivedPaymentId(""))

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findReceivedPayments(
				accessToken,
				offset,
				sortBy,
				query,
				"json",
				limit,
				statuses,
				filteredOrgs,
				types,
				since,
				until,
				accountId,
				customerId,
				fromAmount,
				toAmount,
				"org,customer,account,incomingAch,incomingWire,realtimeReceivedPayment"
			),
		(x) => x.data.length,
		[
			refreshToken,
			since,
			until,
			sortBy,
			[...statuses, ...types, ...filteredOrgs].join(","),
			fromAmount,
			toAmount,
			query,
		],
		`${prefix}-`,
		true
	)

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	const typesFilter = (
		<Filter
			statuses={types}
			setStatuses={setTypes}
			onFilterFunc={() => reset(limit)}
			options={typesOptions}
			title="Type"
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	function toggleSort(columnName: string) {
		const sortParam = sortBy == `-${columnName}` ? columnName : `-${columnName}`
		setSortBy(sortParam)
		reset(limit)
	}

	async function exportPayments() {
		const response = await findReceivedPayments(
			accessToken,
			0,
			sortBy,
			query,
			"csv",
			null,
			statuses,
			filteredOrgs,
			types,
			since,
			until,
			accountId,
			customerId,
			fromAmount,
			toAmount
		)

		response.match(
			(d) => downloadFile("Received Payments.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)
		return true
	}

	return (
		<DataTableCard className={"received-payments-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				dateTimePicker={dateTime}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
					...(enableTypeFilter ? [typesFilter] : []),
				]}
				searchBox={enableSearchBox ? searchBox : null}
				exportFunc={enableExport ? exportPayments : undefined}
				title={enableTitle ? "Received Payments" : null}
				className={classNames(showTabsOnHeader && "with-title-bar-tabs")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: receivedPayments}) => {
					return (
						<ReceivedPaymentsTable
							receivedPayments={receivedPayments.data}
							meta={receivedPayments.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							isUsingPaging={true}
							include={receivedPayments.included}
							fullHeight={fullHeight}
							showTabsOnHeader={showTabsOnHeader}
							includedColumns={includedColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
							selectedReceivedPaymentId={selectedReceivedPaymentId}
							setSelectedReceivedPaymentId={setSelectedReceivedPaymentId}
							disableDrawer={disableDrawer}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
